import {Accordion, AccordionDetails, AccordionSummary, Card, Grid} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';

const NervousSystemDisorder = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={'nervousSystemDisorder-content'}
                id={'nervousSystemDisorder-header'}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Nervous system disorder</div>
                    <div>{counter('nervousSystemDisorder')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'nervousSystemDisorder',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.Narcolepsy.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Narcolepsy</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.narcolepsyDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.narcolepsyDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'narcolepsyPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or specialist?',
                                                    ...parameters.narcolepsyPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ParkinsonsDisease.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Parkinson's Disease</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.parkinsonsDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.parkinsonsDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="parkinsonsTreatment"
                                                label="Provide details including medications you take, the degree of your symptoms, and how this affects your daily activities."
                                                value={parameters.parkinsonsTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.parkinsonsTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.DementiaAlzheimers.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Dementia / Alzheimer's</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'alzheimersDiagnosis',
                                                    label: "Have you been diagnosed with Alzheimer's or Dementia?",
                                                    ...parameters.alzheimersDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.SeizuresEpilepsyPetitMal.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Seizures / Epilepsy - Petit Mal</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.petitmalDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.petitmalDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="petitmalRecentSeizures"
                                                label="How many years since your last seizure?"
                                                value={parameters.petitmalRecentSeizures?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.petitmalRecentSeizures, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="petitmalTreatment"
                                                label="Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?"
                                                value={parameters.petitmalTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.petitmalTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.SeizuresEpilepsyGrandMal.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Seizures / Epilepsy - Grand Mal</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.grandmalDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.grandmalDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="grandmalRecentSeizures"
                                                label="How many years since your last seizure?"
                                                value={parameters.grandmalRecentSeizures?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.grandmalRecentSeizures, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="grandmalTreatment"
                                                label="Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?"
                                                value={parameters.grandmalTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.grandmalTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default NervousSystemDisorder;
